import React, { FC, ReactNode } from "react";
import styled from "styled-components";
import { Device, BundleTileGap } from "./sizes";

const Margin = {
  desktop: 100,
  laptop: 100,
  tablet: 40,
  mobile: 40,
  mobileSm: 20,
};

// (item width * num items in row) + (gap * num gaps in row)
const RowWidth = {
  desktop: 1250, // (275 * 4) + (50 * 3)
  laptop: 1050, // (225 * 4) + (50 * 3)
  tablet: 972, // (225 * 4) + (24 * 3)
  mobile: 621, // (175 * 3) + (48 * 2)
  mobileSm: 360, // (160 * 2) + (40 * 1)
};

// In order to create a grid-like appearance in flexbox (which is needed thanks to IE)
// and to accommodate a variable number of items, we flex-wrap the items, and have
// even left and right margins in order to create a "gap" between the items and so
// they stack nicely. This results in an additional margin on the left and right side
// of the container. We subtract half of the gap from the left and right side to
// account for this.
//
// Example:
// - A row has left and right margins of 100px
// - Items in that row need gaps of 50px
// - To create the gap, we put a 25px left-right margin on each item (50px / 2)
// - This creates an additional 25px margin on either side of the container
// - To accommodate this we subtract the additional 25px from the left and right side
// - 100px - (50px / 2) = 75px
//
const MarginAdjusted = {
  desktop: Margin.desktop - BundleTileGap.desktop / 2,
  laptop: Margin.laptop - BundleTileGap.laptop / 2,
  tablet: Margin.tablet - BundleTileGap.tablet / 2,
  mobile: Margin.mobile - BundleTileGap.mobile / 2,
  mobileSm: Margin.mobileSm - BundleTileGap.mobileSm / 2,
};

// Continuing from above, we need to account for this additional margin so that the
// number of items in a row will fit exactly. We accomplish this by adding the gap
// to the desired width of the row.
//
// Example:
// - We want the row to be 1250px
// - Items in that row need gaps of 50px
// - As explained above there is 25px additional margin on each side
// - To accommodate this we add the additional 25px from the left and right side
// - 1250px + 50px = 1300px
//
const RowWidthAdjusted = {
  desktop: RowWidth.desktop + BundleTileGap.desktop,
  laptop: RowWidth.laptop + BundleTileGap.laptop,
  tablet: RowWidth.tablet + BundleTileGap.tablet,
  mobile: RowWidth.mobile + BundleTileGap.mobile,
  mobileSm: RowWidth.mobileSm + BundleTileGap.mobileSm,
};

const StyledCollectionLayout = styled.div`
  display: flex;
  justify-content: center;
`;

const Section = styled.section`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin: 0 10px;
  width: calc(100vw - 20px);

  @media ${Device.notSmallest} {
    justify-content: flex-start;
  }

  @media ${Device.desktop} {
    width: ${RowWidthAdjusted.desktop}px;
    margin: 0 ${MarginAdjusted.desktop}px;
  }

  @media ${Device.laptop} {
    width: ${RowWidthAdjusted.laptop}px;
    margin: 0 ${MarginAdjusted.laptop}px;
  }

  @media ${Device.tablet} {
    width: ${RowWidthAdjusted.tablet}px;
    margin: 0 ${MarginAdjusted.tablet}px;
  }

  @media ${Device.mobile} {
    width: ${RowWidthAdjusted.mobile}px;
    margin: 0 ${MarginAdjusted.mobile}px;
  }

  @media ${Device.mobileSm} {
    width: ${RowWidthAdjusted.mobileSm}px;
    margin: 0 ${MarginAdjusted.mobileSm}px;
  }
`;

export const CollectionLayout: FC<{ children: ReactNode }> = ({ children }) => (
  <StyledCollectionLayout>
    <Section>{children}</Section>
  </StyledCollectionLayout>
);
